import * as Sentry from '@sentry/react';
import moment from 'moment-timezone';
import { UnknownAction } from 'redux';

import { gengql } from '@/__generated__';
import {
  GetUserActionCreditRateQuery,
  NetworkExpertConnectionState,
} from '@/__generated__/graphql';
import { ERROR_CODES, hasErrorCode } from '@/core/api';
import { PROFILE__UPDATE } from '@/profile/store';
import { removeAttribute } from '@/utils/reducer';

import { AppThunk } from '.';
import { GET_CREDIT_RATE, GET_USER, UPDATE_USER } from './queries';

const USER__UPDATE_INFORMATION = 'USER__UPDATE_INFORMATION';
const USER__DELETE = 'USER__DELETE';

export function updateUserInfo(user: object): UnknownAction {
  return {
    type: USER__UPDATE_INFORMATION,
    user,
  };
}

export function updateUser(
  values: any,
  options?: { signal?: AbortSignal }
): AppThunk<Promise<any>> {
  return async (dispatch, _getState, { graphql }) => {
    const data = await graphql.mutate(UPDATE_USER, values, options);

    if (data) {
      dispatch(updateUserInfo(data.updateUser));
      return data.updateUser;
    }
  };
}

export function updateExpertState(values: any): AppThunk<Promise<any>> {
  return async (dispatch, _getState, { graphql }) => {
    const data = await graphql.mutate(
      gengql(/* GraphQL */ `
        mutation actionUpdateExpertState($id: String!, $expert_state: ExpertState!) {
          updateExpertState(id: $id, expert_state: $expert_state)
        }
      `),
      values
    );

    if (data.updateExpertState) {
      dispatch({
        type: USER__UPDATE_INFORMATION,
        user: values,
      });
    }
  };
}

export function deleteUser(id: any): AppThunk<Promise<any>> {
  return (dispatch, _getState, { graphql }) =>
    graphql
      .mutate(
        gengql(/* GraphQL */ `
          mutation actionDeleteUser($id: String!) {
            deleteUser(id: $id) {
              id
            }
          }
        `),
        { id }
      )
      .then(() =>
        dispatch({
          type: USER__DELETE,
          id,
        })
      );
}

export function fetchUser(
  id: any,
  {
    force = false,

    // user
    stats = false,
    signupSubdomain = false,
    groups = false,
    profileKeywordsDefinition = false,
    recruiter = false,
    otpAuthEnabled = false,

    // profile
    audit = false,
    experiences = false,
    addresses = false,
    expertise = false,
    groupKeywords = false,
    education = false,
    sources = false,
    sharedInternalNetworks = false,
    internalNetworks = false,
    expertRequestCandidates = false,
    availableConsultation = false,
    connectionStates = [NetworkExpertConnectionState.Active],
  } = {}
): AppThunk<Promise<any>> {
  return async (dispatch, getState, { graphql }) => {
    const queryKey = JSON.stringify({
      id,
      stats,
      groups,
      recruiter,
      audit,
      experiences,
      addresses,
      education,
      expertise,
      internalNetworks,
      sources,
      sharedInternalNetworks,
      expertRequestCandidates,
    });
    const params = {
      id: id || getState().viewer.username,
      otpAuthEnabled,
      stats,
      signupSubdomain,
      groups,
      recruiter,
      audit,
      experiences,
      addresses,
      expertise,
      groupKeywords,
      education,
      sources,
      sharedInternalNetworks,
      expertRequestCandidates,
      availableConsultation,
      internalNetworks,
      profileKeywordsDefinition,
      connectionStates,
    };

    if (internalNetworks) {
      params.connectionStates = connectionStates;
    }

    const { data } = await graphql.client.query({
      query: GET_USER,
      variables: params,
      fetchPolicy: force ? 'network-only' : undefined,
    });
    const user = data.user;

    if (user) {
      const creditRate = await dispatch(fetchUserCreditRate(user.id));
      const profile = { ...user.profile, credit_rate: creditRate?.cents };

      dispatch({
        type: USER__UPDATE_INFORMATION,
        queryKey,
        user: { ...user, profile },
      });

      dispatch({
        type: PROFILE__UPDATE,
        queryKey,
        profile,
      });
    }

    return user;
  };
}

export function submitClientInterest(data: any): AppThunk<Promise<any>> {
  return (_dispatch, _getState, { graphql }) =>
    graphql.mutate(
      gengql(/* GraphQL */ `
        mutation actionSubmitClientInterest(
          $name: String!
          $title: String
          $organization: String!
          $email: String!
          $phone: String
          $use_case: String
          $questions: String
          $source: ClientSource!
        ) {
          submitClientInterest(
            name: $name
            title: $title
            organization: $organization
            email: $email
            phone: $phone
            use_case: $use_case
            questions: $questions
            source: $source
          )
        }
      `),
      data
    );
}

/**
 * Fetched the credit rate for a given user taking in context the current user's billing account
 * @param {number} userId
 * @returns {async function(dispatch, getState, {graphql: *}): Promise<{cents: number, currency: string}>}
 */

export function fetchUserCreditRate(
  userId: string
): AppThunk<Promise<NonNullable<GetUserActionCreditRateQuery['expert']>['creditRate']>> {
  return async (_dispatch, getState, { graphql }) => {
    const { userContext, userContextOptions } = getState().ui;

    const billingAccountId =
      userContextOptions?.find((o: any) => o.value === userContext)?.billingAccountId || '';

    try {
      const { data } = await graphql.client.query({
        query: GET_CREDIT_RATE,
        variables: { id: userId, billingAccountId },
      });

      return data.expert?.creditRate || null;
    } catch (err: unknown) {
      // Defense in depth for ENG-5283 (Sentry WEBAPP-BWK). The API itself now
      // nulls the field out when the active billing account can't price in
      // credits (SMB/USD accounts with a 0 per-credit price), so this catch
      // only fires against an API that predates that fix. The rate is
      // genuinely unavailable either way, so honor this thunk's `| null`
      // contract rather than let the rejection break the surrounding user load.
      if (hasErrorCode(err, ERROR_CODES.BILLING_INVALID_CREDIT_PRICING)) {
        Sentry.addBreadcrumb({
          category: 'billing.credit_rate',
          level: 'info',
          message: 'Credit rate unavailable: pricing is misconfigured',
          data: { userId, billingAccountId },
        });
        return null;
      }
      throw err;
    }
  };
}

const INITIAL_STATE = {};

function reduceUser(state = {}, action: any) {
  switch (action.type) {
    case USER__UPDATE_INFORMATION:
      return {
        ...state,
        ...action.user,
      };
    default:
      return state;
  }
}

export default function (state = INITIAL_STATE, action: any) {
  const userId =
    action.type === USER__UPDATE_INFORMATION
      ? // @ts-expect-error TS(2339): Property 'currentId' does not exist on type '{ $$q... Remove this comment to see the full error message
        action.user.id || state.currentId
      : // @ts-expect-error TS(2339): Property 'currentId' does not exist on type '{ $$q... Remove this comment to see the full error message
        action.userId || state.currentId;

  if (!userId) return state;

  if (action.type === USER__DELETE) {
    return removeAttribute(state, action.id);
  }

  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  const newUserState = reduceUser(state[userId], action);
  const newState = {
    ...state,
    [userId]: newUserState,
  };

  if (action.user && action.user.username) {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    newState[action.user.username] = newUserState;
  }

  return newState;
}
